import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { SharedConfigValues, SharedConstants, SharedPropertyNames } from '@app/eds';

@Component({
  selector: 'app-input[placeholder][isRequired][enterPress]',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss']
})
export class InputComponent implements OnInit, OnChanges {
  // This field is a placeholder value of input , it should be pass while calling this component.
  @Input() placeholder: string = SharedConstants.EMPTY_STRING;

  // This field tells whether the input component is reuired or not , should be pass while calling the component.
  @Input() isRequired!: boolean;

  // This field specifies the placeholder size.
  @Input() placeholderSize = SharedConstants.EMPTY_STRING;

  // This field specifies the prefix icon of Input field.
  @Input() prefixIcon: string = SharedConstants.EMPTY_STRING;

  // Max length is used for maximum length
  @Input() maxLength: number = SharedConfigValues.INPUT_MAX_LENGTH;

  // type is used provide type like text, email etc.
  @Input() inputType!: string;

  // Disables the input box
  @Input() isDisabled = false;

  // To show error icon in input box if error exists.
  @Input() hasCustomError = false;

  // To disable close icon in input box or not.
  @Input() disableClose = false;

  // This function will emit the event.target.value on pressing enter key.
  @Output() enterPress: EventEmitter<string> = new EventEmitter<string>();

  // This function will emit the event.target.value on key press.
  @Output() keyPress: EventEmitter<string> = new EventEmitter<string>();

  // This function will emit the event.target.value on key down.
  @Output() keyDown: EventEmitter<string> = new EventEmitter<string>();

  @Output() changeInput: EventEmitter<string> = new EventEmitter<string>();

  @Output() closeIconClick: EventEmitter<any> = new EventEmitter<any>();

  // This function will emit the event.target.value on key up.
  @Output() keyUp: EventEmitter<string> = new EventEmitter<string>();

  // This function will emit the event on input focus.
  @Output() inputOnFocus: EventEmitter<any> = new EventEmitter<any>();

  // This function will emit the event on input blur.
  @Output() inputOnBlur: EventEmitter<any> = new EventEmitter<any>();

  // FormControl value for input field.
  @Input() set control(value: FormControl) {
    if (value !== this.inputText) {
      this.inputText = value;
    }
  }

  inputText: FormControl = new FormControl(
    SharedConstants.EMPTY_STRING,
    Validators.maxLength(this.maxLength)
  );

  ngOnInit() {
    this.toggleEnableAndDisable();
  }

  ngOnChanges(changes: SimpleChanges) {
    changes[SharedPropertyNames.IS_DISABLED] && this.toggleEnableAndDisable();
  }

  /**
   * Enable/disable the input box.
   */
  toggleEnableAndDisable = () =>
    this.isDisabled ? this.inputText.disable() : this.inputText.enable();

  /**
   * This function will call on clicking cancel icon in input field.
   *
   * It will clear the input text.
   */
  onCancel = () => {
    if (!this.disableClose) {
      this.clearInput();
      this.closeIconClick.emit();
    }
  };

  /**
   * Clears input text.
   */
  clearInput = () => this.inputText.setValue(SharedConstants.EMPTY_STRING);

  /**
   * Sets the value provided in the input box
   *
   * @param {string} value
   */
  setInputValue = (value: string) => this.inputText.setValue(value);

  /**
   *  This function will return the Input class on the basis on some condition.
   *
   * @return string
   */
  getInputClass = () =>
    this.isRequired &&
      this.inputText.value === SharedConstants.EMPTY_STRING &&
      this.inputText.touched
      ? 'input-field-touched'
      : 'input-field-untouched';

  /**
   * This function will trigger on pressing the enter key.
   * It will bind the event.target.value to the inputText
   *
   * @param event
   */
  onKeyEnter = (event: any) => this.enterPress.emit(event.target.value);

  /**
   * This function will trigger on Key Press.
   *
   * @param event
   */
  onKeyPress = (event: any) => this.keyPress.emit(event.target.value);

  /**
   * This function will trigger on key down.
   *
   * @param event
   */
  onKeyDown = (event: any) => this.keyDown.emit(event.target.value);

  /**
   * This function will trigger on key up.
   *
   * @param event
   */
  onKeyUp = (event: any) => this.keyUp.emit(event.target.value);

  /**
   * This function is triggered on input change
   *
   * @param event
   */
  onChangeInput = (event: any) => this.changeInput.emit(event.target.value);

  /**
   * This function will trigger when input gets on focus.
   *
   * @param event
   */
  onFocus = (event: any) => this.inputOnFocus.emit(event);

  /**
   * This function will trigger when input gets on blur.
   *
   * @param event
   */
  onBlur = (event: any) => this.inputOnBlur.emit(event);

  /**
   * To show error icon in input box.
   *
   * @returns {boolean} bool
   */
  showErrorIcon = () =>
    this.hasCustomError ||
    (this.isRequired &&
      this.inputText.value === SharedConstants.EMPTY_STRING &&
      this.inputText.touched);
}
